import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { adminField } from "./AdminPages";
import { tx, useLang } from "../../lib/i18n";
import { PageLoading } from "../../components/PageLoading";

const box = "rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-5";
const ICON = { pass: [CheckCircle2, "text-[var(--mv-teal)]"], warn: [AlertTriangle, "text-amber-300"], fail: [XCircle, "text-red-400"] };

function Checks({ data }) { useLang();
  return (
    <div className={box} data-testid="launch-checks">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{tx("Launch readiness")}</h2>
        <span className={`rounded-full px-4 py-1.5 text-sm font-bold ${data.decision === "GO" ? "bg-[var(--mv-teal)] text-black" : "bg-red-500/20 text-red-300"}`} data-testid="launch-decision">{data.decision}</span>
      </div>
      <ul className="mt-4 space-y-2">
        {data.checks.map((c) => { const [I, cls] = ICON[c.status]; return (
          <li key={c.key} className="flex items-start gap-3 text-sm" data-testid={`launch-check-${c.key}`}>
            <I className={`mt-0.5 h-4 w-4 shrink-0 ${cls}`} /><span>{tx(c.label)}{c.detail && <span className="block text-xs text-[var(--mv-dim)]">{tx(c.detail)}</span>}</span>
          </li>); })}
      </ul>
    </div>
  );
}

function Backups() { useLang();
  const [b, setB] = useState([]);
  useEffect(() => { api.get("/admin/launch/backups").then((r) => setB(r.data)).catch(() => {}); }, []);
  const dl = async (n) => {
    try { const r = await api.get(`/admin/launch/backups/${n}.json`, { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `${n}.json`; a.click(); } catch (e) { toast.error(tx(errMsg(e))); }
  };
  if (!b.length) return null;
  return (
    <div className="mt-4 text-xs" data-testid="cleanup-backups"><p className="font-semibold text-[var(--mv-teal)]">{tx("Backups")}</p>
      {b.map((x) => <p key={x.name} className="mt-1">{x.name} · {x.records} {tx("records")} <button onClick={() => dl(x.name)} className="ml-2 text-[var(--mv-teal)] underline" data-testid={`backup-download-${x.name}`}>{tx("Download JSON")}</button></p>)}
    </div>
  );
}

function Cleanup({ onDone }) { useLang();
  const [p, setP] = useState(null);
  const [f, setF] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/launch/cleanup-preview").then((r) => setP(r.data));
  useEffect(() => { load(); }, []);
  if (!p) return null;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/launch/cleanup", f); toast.success(`${tx("Removed")} ${data.total} · ${tx("backup")}: ${data.backup_collection}`); setF({ password: "", confirm: "" }); load(); onDone(); } catch (e) { toast.error(tx(errMsg(e))); }
    setBusy(false);
  };
  return (
    <div className={box} data-testid="launch-cleanup">
      <h3 className="font-semibold">{tx("Clean production data")}</h3>
      <p className="mt-1 text-xs text-[var(--mv-dim)]">{tx("Preview of test and simulated records that will be backed up, then removed. Run this on the published site after the first publish.")}</p>
      {p.blocked && <p className="mt-3 text-sm text-red-400">{tx(p.blocked)}</p>}
      <p className="mt-4 text-sm" data-testid="cleanup-total">{tx("Records to remove")}: <b>{p.total}</b> · {tx("test accounts")}: <b>{p.removed_accounts}</b></p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs">{Object.entries(p.counts).map(([k, v]) => <span key={k} className="rounded-full border border-[var(--mv-border-2)] px-3 py-1" data-testid={`cleanup-count-${k}`}>{k}: {v}</span>)}</div>
      {p.removed_list.length > 0 && (
        <details className="mt-3 text-xs" data-testid="cleanup-removed-list">
          <summary className="cursor-pointer text-[var(--mv-teal)]">{tx("Show every account that will be removed")} ({p.removed_list.length})</summary>
          <ul className="mt-2 max-h-64 overflow-auto rounded-lg border border-[var(--mv-border)] p-2 text-[var(--mv-muted)]">{p.removed_list.map((a) => <li key={a.email}>{a.email} · {tx(a.role)} · {tx(a.reason)}</li>)}</ul>
        </details>
      )}
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <div><p className="text-xs font-semibold text-[var(--mv-teal)]">{tx("Accounts kept")}</p><ul className="mt-1 text-xs text-[var(--mv-muted)]" data-testid="cleanup-kept-accounts">{p.kept_accounts.map((a) => <li key={a.email}>{a.email} · {tx(a.role)}</li>)}</ul></div>
        <div><p className="text-xs font-semibold text-[var(--mv-teal)]">{tx("Always kept")}</p><ul className="mt-1 list-disc pl-4 text-xs text-[var(--mv-muted)]">{p.keeps.map((k) => <li key={k}>{tx(k)}</li>)}</ul></div>
      </div>
      <Backups />
      {p.total > 0 && !p.blocked && (
        <div className="mt-5 grid gap-3 md:grid-cols-3">
          <input type="password" className={adminField} placeholder={tx("Super Admin password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="cleanup-password" />
          <input className={adminField} placeholder="DELETE TEST DATA" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="cleanup-confirm" />
          <button disabled={busy || f.confirm !== "DELETE TEST DATA" || !f.password} onClick={run} className="rounded-lg bg-red-500/80 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" data-testid="cleanup-run">{tx(busy ? "Working…" : "Back up & remove test data")}</button>
        </div>
      )}
    </div>
  );
}

export default function LaunchReadiness() { useLang();
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/launch/checks").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <p className="text-sm text-[var(--mv-dim)]"><PageLoading /></p>;
  return <div className="space-y-5" data-testid="launch-readiness"><Checks data={d} /><Cleanup onDone={load} /></div>;
}
